import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, Input, OnChanges, Optional, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';

import { ScoreboardFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { LeaderboardGameScore } from '@frontend/sports/betting-offer/feature/model';
import { GridConfig, LiveSectionConfig, Sitecore, SitecoreGameMessagesConfig } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { GridEvent } from '@frontend/sports/grid/core/feature/model';
import { ModalRef } from '@frontend/sports/modal/feature';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { MarketUrlParam, UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { NativeAppService } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { ReplaySubject, filter, map, switchMap } from 'rxjs';

import { BetBuilderConfigService } from '../../betbuilder/services/betbuilder-config.service';
import { getBetbuilderTypeValue, isBetbuilderEvent } from '../../betbuilder/utils';
import { EpcotConfigService } from '../../common/epcot-config.service';
import { BetBuilderOverlayHeaderComponent } from '../../event-details/bet-builder-v2/bet-builder-overlay-header/bet-builder-overlay-header.component';
import { BetBuilderTrackingValues } from '../../event-details/bet-builder-v2/bet-builder-tracking';
import { BetBuilderOverlayService } from '../../event-details/bet-builder-v2/services/bet-builder-overlay.service';
import { AlertButtonComponent } from '../../native-alerts/alert-button/alert-button.component';
import { PriceboostCountSignpostComponent } from '../../shared-priceboost/Components/priceboost-count-signpost.component';
import { SignpostTypeService } from '../../shared-priceboost/Services/signpost-type.service';
import { EventTimerComponent } from '../../timers/event-timer.component';
import { RaceInfoComponent } from './race-info.component';

@Component({
    selector: 'ms-event-info',
    templateUrl: 'event-info.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        RaceInfoComponent,
        IconCustomComponent,
        EventTimerComponent,
        AsyncPipe,
        BetBuilderOverlayHeaderComponent,
        PriceboostCountSignpostComponent,
        AlertButtonComponent,
    ],
})
export class EventInfoComponent implements OnChanges {
    @Input() event: GridEvent;
    @Input() isOutrightMarket: boolean;
    @Input() mediaEnabled: boolean;
    @Input() disableBadges = false;
    @Input() hideFavouriteIcon = false;
    @Input() showEventTimerInfoSection?: boolean = true;
    @Input() showFixedFooterSection?: boolean = true;
    @HostBinding('class') className = 'grid-event-info';
    modal: ModalRef;
    showIcon: boolean;
    liveIconText: string;
    isLeagueChildPageDesign: boolean;
    liveEventInfo?: string;
    private event$ = new ReplaySubject<GridEvent>();
    broadcaster$ = this.event$.pipe(
        filter((event) => !event?.scoreboard?.started),
        switchMap((event) => this.sitecoreGameMessage.whenReady.pipe(map(() => this.sitecoreGameMessage.secondaryBroadcastInfo?.messages[event.id]))),
    );

    @ViewChild('header') headerTemplate: TemplateRef<any>;

    private enableSGPNavigationOverride: boolean;

    priceBoostCount?: number;

    constructor(
        public sitecoreGameMessage: SitecoreGameMessagesConfig,
        public sitecore: Sitecore,
        private urlHelper: UrlHelperService,
        private redirectHelperService: RedirectHelperService,
        private betBuilderOverlayService: BetBuilderOverlayService,
        private betBuilderConfigService: BetBuilderConfigService,
        private tracker: TrackingService,
        private nativeAppservice: NativeAppService,
        private epcotConfigService: EpcotConfigService,
        private scoreboardFactory: ScoreboardFactory,
        gridConfig: GridConfig,
        private liveSectionConfig: LiveSectionConfig,
        @Optional() private signpostTypeService?: SignpostTypeService,
    ) {
        this.showIcon = this.nativeAppservice.isTerminal;
        this.liveIconText =
            (this.epcotConfigService.isEnabled() && gridConfig.is2way3wayEnabled) || gridConfig.gridEventDisplayVersion === 'V2'
                ? ''
                : this.sitecore.event.Live;
        this.isLeagueChildPageDesign = gridConfig.isLeagueChildPageDesign;
        this.enableSGPNavigationOverride = gridConfig.enableSGPNavigationOverride;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.event) {
            this.liveEventInfo = this.getLiveEventInfo(this.event.sport.id, this.event.league.parentLeagueId);
            this.priceBoostCount = this.getPriceBoost();
            this.event$.next(this.event);
        }
    }

    get raceInfo(): boolean {
        const score = this.event.scoreboard.getScore<LeaderboardGameScore>()!;
        const raceSport = this.event.sport.id === SportConstant.Formula1 || this.event.sport.id === SportConstant.Motorbikes;

        return raceSport && this.event.scoreboard.started && score && score.type && !score.preRace;
    }

    get showBetBuilderIcon(): boolean {
        return !this.disableBadges && isBetbuilderEvent(this.event, this.betBuilderConfigService) && !!this.showFixedFooterSection;
    }

    builderClick($event: MouseEvent) {
        $event.stopPropagation();
        $event.preventDefault();

        this.tracker.track(trackingConstants.EVENT_TRACKING, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetBuilderTrackingValues.BetBuilder,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetBuilderTrackingValues.BuildaBetPod,
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetBuilderTrackingValues.Click,
            [trackingConstants.COMPONENT_POSITION_EVENT]: BetBuilderTrackingValues.ProductIcon,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.event.name,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: BetBuilderTrackingValues.BuildaBet,
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
            [trackingConstants.SPORT_ID]: this.event.sport.id.toString(),
            [trackingConstants.SPORT_LEAGUE_ID]: this.event.league.id.toString(),
        });

        if (this.betBuilderConfigService.showEntainUiInOverlay(this.event.sport.id)) {
            this.openEntainBetBuilderOverlay();
        } else {
            this.openEventPageBetBuilderMarket();
        }
    }

    openEntainBetBuilderOverlay() {
        this.betBuilderOverlayService.openBuilder({
            headerTemplate: this.headerTemplate,
            event: this.event,
            source: BetBuilderTrackingValues.EventInfo,
            isOverlay: true,
        });
    }

    openEventPageBetBuilderMarket() {
        const eventUrl = this.enableSGPNavigationOverride
            ? this.urlHelper.getEventUrl(this.event)
            : this.urlHelper.getEventUrl(this.event, getBetbuilderTypeValue(this.event, MarketUrlParam));
        this.redirectHelperService.goToPage(eventUrl);
    }

    private getLiveEventInfo(sportsId: number, leagueId?: number): string | undefined {
        const scoreboard = this.event.scoreboard;
        if (!scoreboard.started || !leagueId || !this.liveSectionConfig.liveEventsInfoForLeagueIds.includes(leagueId)) {
            return;
        }

        return this.scoreboardFactory.getSportsLiveEventInfo(sportsId, scoreboard);
    }

    getPriceBoost(): number | undefined {
        if (this.signpostTypeService?.isPriceBoost && this.showFixedFooterSection && this.event.priceBoostCount && this.event.priceBoostCount > 0) {
            return this.event.priceBoostCount;
        }

        return undefined;
    }
}
